import type { PageDefinition, RelationType } from "@judo/model-api";

/**
 * Check if a page is a singleton access page.
 *
 * A singleton access page is an AccessViewPage whose dataElement is a RelationType with:
 * - isAccess = true (it's an access relation, i.e., top-level entry point)
 * - isCollection = false (there's exactly one instance, not a list)
 *
 * Singleton access pages don't require a signedIdentifier in the URL because
 * there's only one transfer behind the relation. The transfer's signedIdentifier
 * must be resolved at runtime by calling refreshAccessRelation before the
 * normal onInit refresh can proceed.
 *
 * @param page - The PageDefinition to check
 * @returns true if the page is a singleton access page
 */
export function isSingletonAccessPage(page: PageDefinition): boolean {
	const dataElement = page.dataElement;

	if (!dataElement || dataElement["@type"] !== "RelationType") {
		return false;
	}

	const relation = dataElement as RelationType;

	// isAccess can be boolean true or string "true" depending on model parsing
	const isAccess = String(relation.isAccess) === "true";
	// isCollection defaults to true in the EMF model, so false means explicitly set
	const isNotCollection = String(relation.isCollection) === "false";

	return isAccess && isNotCollection;
}

/**
 * Get the access relation from a singleton access page.
 *
 * @param page - The PageDefinition (must be a singleton access page)
 * @returns The RelationType representing the access relation, or undefined
 */
export function getSingletonAccessRelation(page: PageDefinition): RelationType | undefined {
	if (!isSingletonAccessPage(page)) {
		return undefined;
	}
	return page.dataElement as RelationType;
}
